import React from 'react';
import {Table, TableBody, TableCell, TableContainer, TableRow, Typography, Box, Stack, Paper, useTheme, useMediaQuery, TableHead} from '@mui/material';
import StatusBadge from '../../components/common/StatusBadge';
import {formatIsoToHHMM} from '../../common/DateUtil';
import {useTranslation} from "react-i18next";

export default function UpcomingAppointmentsTable({items = [], onRowClick}) {
    const theme = useTheme();
    const {t} = useTranslation();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    if (!items?.length) return null;

    if (isMobile) {
        return (
            <Stack spacing={1}>
                {items.map((appointment) => {
                    const id = appointment.id;
                    const start = formatIsoToHHMM(appointment.startAt);
                    const end = formatIsoToHHMM(appointment.endAt);
                    const petName = appointment?.animal?.name;
                    const staff = appointment?.staff?.person?.forename;
                    const service = appointment.service.name;
                    const status = appointment?.status;

                    return (
                        <Paper
                            key={id || Math.random()}
                            variant="outlined"
                            sx={{p: 1.5}}
                            onClick={() => id && onRowClick && onRowClick(appointment)}
                            data-testid={`upcoming-appointment-card-${petName}`}
                        >
                            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                                <Box sx={{minWidth: 0}}>
                                    <Typography variant="body2" sx={{fontWeight: 600}}>{start} – {end}</Typography>
                                    <Typography variant="body2" color="primary" noWrap>{petName}</Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap>{service}</Typography>
                                    <Typography variant="caption" color="text.secondary" display="block" noWrap>{staff}</Typography>
                                </Box>
                                <Box flexShrink={0}>
                                    <StatusBadge status={status}/>
                                </Box>
                            </Box>
                        </Paper>
                    );
                })}
            </Stack>
        );
    }

    function TimelineBar({ beforeMin, apptMin, afterMin, startAt, endAt }) {
        const total = Math.max(1, beforeMin + apptMin + afterMin);
        const beforePct = (beforeMin / total) * 100;
        const apptPct = (apptMin / total) * 100;
        const afterPct = (afterMin / total) * 100;

        return (
            <Box sx={{ width: '100%' }}>
                {/* Row 1: start/end times */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {startAt}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {endAt}
                    </Typography>
                </Box>

                {/* The bar */}
                <Box
                    sx={{
                        height: 10,
                        borderRadius: 999,
                        overflow: 'hidden',
                        display: 'flex',
                        border: '1px solid',
                        borderColor: 'divider',
                        width: '100%',
                        backgroundColor: 'background.paper'
                    }}
                    aria-label="Buffer en afspraak tijdlijn"
                >
                    {/* Buffer voor (grey) */}
                    <Box
                        sx={{
                            width: `${beforePct}%`,
                            backgroundColor: 'grey.300',
                            backgroundImage:
                                'repeating-linear-gradient(135deg, rgba(0,0,0,0.10) 0, rgba(0,0,0,0.10) 6px, transparent 6px, transparent 12px)'
                        }}
                        title={`Buffer voor: ${beforeMin} min`}
                    />

                    {/* Afspraak */}
                    <Box
                        sx={{
                            width: `${apptPct}%`,
                            backgroundColor: 'primary.main',
                            opacity: 0.85
                        }}
                        title={`Afspraak: ${apptMin} min`}
                    />

                    {/* Buffer na (grey) */}
                    <Box
                        sx={{
                            width: `${afterPct}%`,
                            backgroundColor: 'grey.300',
                            backgroundImage:
                                'repeating-linear-gradient(135deg, rgba(0,0,0,0.10) 0, rgba(0,0,0,0.10) 6px, transparent 6px, transparent 12px)'
                        }}
                        title={`Buffer na: ${afterMin} min`}
                    />
                </Box>

                {/* Row 2: buffer minutes left/right */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {beforeMin > 0 ? `+${beforeMin}m` : ''}
                    </Typography>

                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {afterMin > 0 ? `+${afterMin}m` : ''}
                    </Typography>
                </Box>
            </Box>
        );
    }


    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>{t("startTime") + ' - ' + t("endTime")}</TableCell>
                        <TableCell>{t("animalNameLabel")}</TableCell>
                        <TableCell>{t("service")}</TableCell>
                        <TableCell>{t("employee")}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {items.map((appointment) => {
                        const id = appointment.id;
                        const start = formatIsoToHHMM(appointment.startAt);
                        const end = formatIsoToHHMM(appointment.endAt);
                        const petName = appointment?.animal?.name;
                        const staff = appointment?.staff?.person?.forename;
                        const service = appointment.service;
                        const status = appointment?.status;

                        const startAt = new Date(appointment.startAt);
                        const endAt = new Date(appointment.endAt);

                        const totalMinutes = Math.round((endAt - startAt) / 60000);

                        return (
                            <TableRow key={id || Math.random()} hover sx={{cursor: id ? 'pointer' : 'default'}} onClick={() => id && onRowClick && onRowClick(appointment)} data-testid={`upcoming-appointment-row-${petName}`}>
                                <TableCell sx={{whiteSpace: 'nowrap'}}>
                                    {/*<Typography variant="body2">{start} – {end}</Typography>*/}
                                    <TimelineBar afterMin={service?.bufferAfterMinutes} apptMin={totalMinutes} beforeMin={service?.bufferAfterMinutes} startAt={start} endAt={end}/>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" color="primary">{petName}</Typography>
                                </TableCell>
                                <TableCell sx={{whiteSpace: 'nowrap'}}>
                                    <Typography variant="body2">{service.name}</Typography>
                                </TableCell>
                                <TableCell sx={{whiteSpace: 'nowrap'}}>
                                    <Typography variant="body2">{staff}</Typography>
                                </TableCell>
                                <TableCell align="right" sx={{whiteSpace: 'nowrap'}}>
                                    <StatusBadge status={status}/>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
